import { useStore } from "@tanstack/react-form";
import { AnimatePresence, motion } from "motion/react";
import React from "react";

import { trpc } from "@blis/trpc/react";

import { Tag, TagGroup, TagList } from "@/components/ui/tag-group";
import { useProfileSettings } from "@/features/auth/hooks/profile";
import { useFormPrimaryOrg } from "../form/common";
import { useAccessionForm } from "./AccessionFormProvider";
import { AccessioningNotes } from "./AccessioningNotes";
import { BillToSelect } from "./components/BillToSelect";
import { EmailSelect } from "./components/EmailSelect";
import { PrimaryClientSelect } from "./components/PrimaryClientSelect";
import { ReceivingOrgSelect } from "./components/ReceivingOrgSelect";
import { SharedOrgSelect } from "./components/SharedOrgSelect";
import { SplitBillingSelect } from "./components/SplitBillingSelect";
import { FormField, FormSection } from "./layout";

export function OrgSection() {
  const { form } = useAccessionForm();
  const meta = useStore(form.store, (s) => s.fieldMeta.orgId);
  return (
    <>
      <FormSection
        title="Client"
        description="Select the primary client and any additional clients."
        actions={<PrimaryClientField />}
        error={meta?.errors?.join(", ")}
      >
        <BillToSection />
        <SplitBillingSection />
        <ClientSection />
        <ReceivingOrgSection />
      </FormSection>
      <AccessioningNotes />
    </>
  );
}

function ClientSection() {
  const { data } = trpc.orgs.list.useQuery();
  const { form } = useAccessionForm();
  const sharedOrgs = useStore(form.store, (s) => s.values.sharedOrgs);
  const emails = useStore(form.store, (s) => s.values.additionalEmails);

  const orgs = data || [];

  const hasItems = !!(sharedOrgs?.length || emails?.length);

  return (
    <div className="flex flex-col p-4">
      <FormField
        title="Share with"
        description="Send reports to these clients and/or email addresses"
        className="z-10"
      >
        <ShareWithSelects />
      </FormField>
      <AnimatePresence>
        {hasItems && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-background"
          >
            <div className="flex items-center gap-2 flex-wrap justify-start border-t pt-4 mt-3">
              <AnimatePresence>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <TagGroup
                    aria-label="Shared clients and emails"
                    color="primary"
                    variant="outline"
                    onRemove={(keys) => {
                      const currentOrgs = form.getFieldValue("sharedOrgs");
                      const currentEmails =
                        form.getFieldValue("additionalEmails");
                      form.setFieldValue(
                        "sharedOrgs",
                        currentOrgs?.filter((s) => !keys.has(s)),
                      );
                      form.setFieldValue(
                        "additionalEmails",
                        currentEmails?.filter((e) => !keys.has(e)),
                      );
                    }}
                  >
                    <TagList>
                      {sharedOrgs?.map((id) => {
                        const org = orgs.find((o) => o.id === id);
                        return (
                          <Tag key={id} id={id}>
                            {org?.name}
                          </Tag>
                        );
                      })}
                      {emails?.map((email, _idx) => (
                        <Tag key={email} id={email}>
                          {email}
                        </Tag>
                      ))}
                    </TagList>
                  </TagGroup>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SplitBillingSection() {
  const { data } = trpc.orgs.list.useQuery();
  const { form } = useAccessionForm();
  const splitBillingOrgs = useStore(
    form.store,
    (s) => s.values.splitBillingOrgs,
  );

  const orgs = data || [];

  const hasItems = !!splitBillingOrgs?.length;

  return (
    <div className="flex flex-col p-4">
      <FormField
        title="Split billing"
        description="Split billing with these clients"
        className="z-10"
      >
        <SplitBillingField />
      </FormField>
      <AnimatePresence>
        {hasItems && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-background"
          >
            <div className="flex items-center gap-2 flex-wrap justify-start border-t pt-4 mt-3">
              <AnimatePresence>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <TagGroup
                    aria-label="Split billing clients"
                    color="primary"
                    variant="outline"
                    onRemove={(keys) => {
                      const currentOrgs =
                        form.getFieldValue("splitBillingOrgs");
                      form.setFieldValue(
                        "splitBillingOrgs",
                        currentOrgs?.filter((s) => !keys.has(s)),
                      );
                    }}
                  >
                    <TagList>
                      {splitBillingOrgs?.map((id) => {
                        const org = orgs.find((o) => o.id === id);
                        return (
                          <Tag key={id} id={id}>
                            {org?.name}
                          </Tag>
                        );
                      })}
                    </TagList>
                  </TagGroup>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PrimaryClientField() {
  const { form } = useAccessionForm();
  const primaryOrg = useFormPrimaryOrg();
  const { state: profile } = useProfileSettings();
  const subjectId = useStore(form.store, (s) => s.values.subjectId);

  const bestGuess = trpc.orgs.guessOrg.useQuery(
    { subjectId },
    {
      enabled: !!primaryOrg.org || !!subjectId,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  React.useEffect(() => {
    if (bestGuess.data) {
      form.setFieldValue("orgId", bestGuess.data);
      form.validateField("orgId", "change");
    } else if (profile.defaultClient) {
      form.setFieldValue("orgId", profile.defaultClient);
      form.validateField("orgId", "change");
    } else if (primaryOrg.org) {
      form.setFieldValue("orgId", primaryOrg.org.id);
      form.validateField("orgId", "change");
    }
  }, [bestGuess.data, profile.defaultClient, form, primaryOrg.org]);

  return (
    <form.Field name="orgId">
      {(field) => (
        <PrimaryClientSelect
          value={field.state.value}
          onChange={(value) => {
            field.handleChange(value);
            form.setFieldValue("billToOrgId", undefined);
            form.setFieldValue("splitBillingOrgs", []);
            form.setFieldValue("sharedOrgs", []);
          }}
        />
      )}
    </form.Field>
  );
}

function SharedOrgField() {
  const { form } = useAccessionForm();
  const orgId = useStore(form.store, (s) => s.values.orgId);

  return (
    <form.Field name="sharedOrgs">
      {(field) => (
        <SharedOrgSelect
          value={field.state.value ?? []}
          onChange={(value) => {
            field.handleChange(value);
          }}
          orgId={orgId}
          placeholder="Client"
        />
      )}
    </form.Field>
  );
}

function AdditionalEmailField() {
  const { form } = useAccessionForm();

  return (
    <form.Field name="additionalEmails">
      {(field) => (
        <EmailSelect
          value={field.state.value ?? []}
          onValueChange={(emails) => {
            field.handleChange(emails);
          }}
          placeholder="Email"
        />
      )}
    </form.Field>
  );
}

function ShareWithSelects() {
  return (
    <div className="flex items-center gap-2 justify-end">
      <SharedOrgField />
      <AdditionalEmailField />
    </div>
  );
}

function SplitBillingField() {
  const { form } = useAccessionForm();
  const orgId = useStore(form.store, (s) => s.values.orgId);

  return (
    <form.Field name="splitBillingOrgs">
      {(field) => (
        <SplitBillingSelect
          value={field.state.value ?? []}
          onChange={(value) => {
            field.handleChange(value);
          }}
          orgId={orgId}
        />
      )}
    </form.Field>
  );
}

function ReceivingOrgSection() {
  const { form } = useAccessionForm();
  const receivingOrgMeta = useStore(
    form.store,
    (s) => s.fieldMeta.receivingOrgId,
  );
  return (
    <div className="flex flex-col p-4">
      <FormField
        title="Receiving lab"
        description="Samples will be sent to this lab"
        className="z-10"
        error={receivingOrgMeta?.errors?.join(", ")}
      >
        <ReceivingOrgField />
      </FormField>
    </div>
  );
}

function BillToSection() {
  return (
    <div className="flex flex-col p-4">
      <FormField
        title="Bill to"
        description="Select a different organization to receive the invoice"
        className="z-10"
      >
        <BillToField />
      </FormField>
    </div>
  );
}

function BillToField() {
  const { form } = useAccessionForm();
  const orgId = useStore(form.store, (s) => s.values.orgId);

  return (
    <form.Field name="billToOrgId">
      {(field) => (
        <BillToSelect
          value={field.state.value}
          onChange={(value) => {
            field.handleChange(value);
          }}
          orgId={orgId}
        />
      )}
    </form.Field>
  );
}

function ReceivingOrgField() {
  const { form } = useAccessionForm();

  return (
    <form.Field name="receivingOrgId">
      {(field) => (
        <ReceivingOrgSelect
          value={field.state.value}
          onChange={field.handleChange}
        />
      )}
    </form.Field>
  );
}
